import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useWizardStore } from '../wizardStore';
import { subscriptionApi, type CreateSubscriptionPayload } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { haptics } from '@/shared/lib/haptics';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };


function periodLabel(period: string, t: ReturnType<typeof useT>, customInterval: number, customUnit: string) {
  if (period === 'monthly') return t('wizard.price.monthly');
  if (period === 'quarterly') return t('wizard.price.quarterly');
  if (period === 'yearly') return t('wizard.price.yearly');
  return `${t('wizard.price.every')} ${customInterval} ${t(`wizard.price.${customUnit}`)}`;
}

function splitLabel(mode: string, t: ReturnType<typeof useT>) {
  if (mode === 'equal') return t('wizard.split.equal');
  if (mode === 'proportional') return t('wizard.split.proportional');
  return t('wizard.split.fixed');
}

function privacyLabel(privacy: string, t: ReturnType<typeof useT>) {
  if (privacy === 'everyone') return t('wizard.members.everyone');
  if (privacy === 'count_only') return t('wizard.members.countOnly');
  return t('wizard.members.nobody');
}

function calcDeadlineShift(mode: string, days: number, dir: string): number {
  if (mode === 'same') return 0;
  if (mode === 'before') return -1;
  return dir === 'before' ? -days : days;
}

function buildPeriod(period: string, customInterval: number, customUnit: string) {
  if (period === 'monthly') return { interval: 1, unit: 'months' };
  if (period === 'quarterly') return { interval: 3, unit: 'months' };
  if (period === 'yearly') return { interval: 12, unit: 'months' };
  return { interval: customInterval, unit: customUnit };
}

function buildPayload(w: ReturnType<typeof useWizardStore.getState>): CreateSubscriptionPayload {
  return {
    name: w.name,
    icon: w.icon,
    type: w.type,
    total_price: parseFloat(w.totalPrice) || 0,
    currency: w.currency,
    period: buildPeriod(w.period, w.customInterval, w.customUnit),
    billing_date: w.billingDate,
    deadline_shift: calcDeadlineShift(w.deadlineMode, w.deadlineDays, w.deadlineDirection),
    split_mode: w.splitMode,
    privacy: w.privacy,
    show_history: w.showHistory,
    draft_step: 'preview',
    member_slots: w.memberSlots.map((s) => ({ name: s.name, amount: parseFloat(s.amount) || 0 })),
  };
}

function Row({ label, value, mono = false, last = false }: { label: string; value: string; mono?: boolean; last?: boolean }) {
  return (
    <div className={cn('flex justify-between text-[14px]', !last && 'mb-1.5')}>
      <span className="text-text-secondary">{label}</span>
      <span className={cn('font-semibold', mono && 'font-mono tabular-nums')}>{value}</span>
    </div>
  );
}

export function PreviewStep() {
  const navigate = useNavigate();
  const store = useWizardStore();
  const { name, icon, totalPrice, currency, period, billingDate, splitMode, privacy, memberSlots, customInterval, customUnit, reset } = store;
  const t = useT();

  const sym = CURRENCY_SYMBOL[currency] ?? currency;
  const total = parseFloat(totalPrice) || 0;

  // Save draft: subscription already exists as draft, just navigate.
  const saveDraft = () => {
    if (store.subscriptionId) {
      saveDraftMutation.mutate(store.subscriptionId);
    } else {
      // Fallback: create draft if somehow we don't have an id yet
      saveDraftFallbackMutation.mutate();
    }
  };

  const saveDraftMutation = useMutation({
    mutationFn: (id: string) => subscriptionApi.patch(id, { draft_step: 'preview' }),
    onSuccess: (sub) => {
      haptics.success();
      reset();
      navigate(`/create?draft=${sub.id}`);
    },
    onError: () => haptics.error(),
  });

  const saveDraftFallbackMutation = useMutation({
    mutationFn: () => subscriptionApi.create(buildPayload(store)),
    onSuccess: (sub) => {
      haptics.success();
      reset();
      navigate(`/create?draft=${sub.id}`);
    },
    onError: () => haptics.error(),
  });

  // Create and notify: activate existing draft, or create plus activate as fallback.
  const createAndActivateMutation = useMutation({
    mutationFn: async () => {
      const id = store.subscriptionId;
      if (id) return subscriptionApi.activate(id);
      // Fallback: create then activate
      const sub = await subscriptionApi.create(buildPayload(store));
      return subscriptionApi.activate(sub.id);
    },
    onSuccess: (sub) => {
      haptics.success();
      reset();
      navigate(`/subscriptions/${sub.id}`);
    },
    onError: () => haptics.error(),
  });

  const isPending = saveDraftMutation.isPending || saveDraftFallbackMutation.isPending || createAndActivateMutation.isPending;
  const isError = saveDraftMutation.isError || saveDraftFallbackMutation.isError || createAndActivateMutation.isError;

  const autoLetter = name.trim() ? name.trim()[0].toUpperCase() : '?';
  const isEmoji = icon.length > 0 && !/^[A-Za-z0-9]/.test(icon);

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h1 className="text-[24px] font-bold tracking-tight">{t('wizard.preview.title')}</h1>

      {/* Header */}
      <div className="flex items-center gap-3">
        {isEmoji ? (
          <span className="w-[44px] h-[44px] rounded-[12px] bg-brand-gradient flex items-center justify-center text-[26px] shadow-[0_4px_10px_rgba(18,120,90,0.28)] flex-shrink-0 leading-none">
            {icon}
          </span>
        ) : (
          <SubscriptionIcon letter={icon || autoLetter} size="lg" />
        )}
        <div className="min-w-0">
          <p className="text-[17px] font-bold truncate">{name}</p>
          <p className="text-[13px] text-text-secondary">{periodLabel(period, t, customInterval, customUnit)}</p>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-[16px] p-4 bg-brand-green/10 border border-brand-green/35">
        <Row label={t('wizard.preview.totalPerPeriod')} value={`${total.toLocaleString('ru')} ${sym}`} mono />
        <Row label={t('wizard.preview.chargeDate')} value={new Date(billingDate + 'T00:00:00').toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' })} />
        <Row label={t('wizard.preview.splitMode')} value={splitLabel(splitMode, t)} />
        <Row label={t('wizard.preview.privacy')} value={privacyLabel(privacy, t)} last />
      </div>

      {/* Fixed-split breakdown */}
      {splitMode === 'fixed' && memberSlots.length > 0 && (
        <>
          <p className="text-[14px] font-semibold text-text-secondary">{t('wizard.preview.perMember')}</p>
          <div className="rounded-[16px] overflow-hidden bg-white/50 border border-white/55">
            {memberSlots.map((slot, i) => (
              <div
                key={slot.id}
                className={cn(
                  'flex justify-between items-center px-4 py-[11px] text-[15px]',
                  i < memberSlots.length - 1 && 'border-b border-black/[0.07]',
                )}
              >
                <span>{slot.name || t('wizard.preview.memberFallback', { n: String(i + 1) })}</span>
                <span className="font-mono font-semibold tabular-nums">
                  {(parseFloat(slot.amount) || 0).toLocaleString('ru')} {sym}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {isError && (
        <p className="text-[13px] text-status-debt text-center">{t('wizard.preview.error')}</p>
      )}

      <div className="flex-1" />

      <WizardFooter>
        <button
          className="text-center text-[14px] text-text-secondary font-medium py-1.5 disabled:opacity-50"
          onClick={saveDraft}
          disabled={isPending}
        >
          {t('wizard.preview.saveDraft')}
        </button>
        <PrimaryButton onClick={() => createAndActivateMutation.mutate()} loading={createAndActivateMutation.isPending} disabled={isPending}>
          {t('wizard.preview.create')}
        </PrimaryButton>
      </WizardFooter>
    </div>
  );
}
